import * as React from 'react';
import { FC, memo, useCallback } from 'react';

import { useRouter } from 'expo-router';

import { OfferListItem } from 'components/offer-list-item/offer-list-item';
import Picture from 'components/picture';
import { buildStoreRestrictionLocatorParams } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/build-store-restriction-locator-params';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import {
  isActiveWalmartUser,
  isWalmartOffer,
  isWalmartPaidMember,
  isWalmartQuarterlyOffer,
} from 'state/loyalty/hooks/utils/walmart';
import { OffersServiceMode } from 'state/loyalty/offers/types';
import { IncentiveSource } from 'state/loyalty/types';
import { TraceName, stopTrace } from 'utils/performance/trace';
import { routes } from 'utils/routing';

import { IncentiveCardDescription } from '../incentive-card/incentive-card.styled';
import { ParsedRewardData } from '../incentive-details/types';

export interface ILoyaltyIOffersItem {
  handlePress: () => void;
  incentiveData: ParsedRewardData;
  deeplinkUnavailablePresentation?: boolean;
}

export const LoyaltyOffersItem: FC<ILoyaltyIOffersItem> = memo(
  ({ handlePress, incentiveData, deeplinkUnavailablePresentation = false }) => {
    const { navigate } = useRouter();
    const { loyaltyUser } = useLoyaltyUser();
    const enableWalmartPlusIntegration = useFlag(LaunchDarklyFlag.ENABLE_WALMART_PLUS_INTEGRATION);
    const isWalmartUser = enableWalmartPlusIntegration && isActiveWalmartUser(loyaltyUser);

    const {
      image,
      imageDescription,
      name: incentiveName,
      description,
      offerServiceMode,
      metadata,
      inlineAlertMessage,
      endDate,
      expirationDate,
      loyaltyEngineId,
      storeRestrictionMode,
      availableStoreIds,
    } = incentiveData;

    const showPromoCodeLabel = metadata?.some(
      data => data?.value === IncentiveSource.PROMOTION_CODE
    );

    const promoCodeLabel = showPromoCodeLabel ? t('promoCodeBadgeLabel') : undefined;

    const onDisplay = useCallback(() => stopTrace(TraceName.OFFERS_LOAD), []);

    // Prefer the offer's own unavailable reason (e.g. a delivery-only message) so the
    // badge explains why it cannot be redeemed here, and fall back to generic copy
    // when neither the service nor the frontend rules resolved a message.
    const unavailableBadgeLabel = deeplinkUnavailablePresentation
      ? inlineAlertMessage || t('notAvailableHere')
      : undefined;

    const handleUnavailableActionPress = useCallback(() => {
      const params: Record<string, string> = {
        redirectUrl: routes.offers,
        ...buildStoreRestrictionLocatorParams({ storeRestrictionMode, availableStoreIds }),
      };
      if (loyaltyEngineId) {
        params.offerEngineId = loyaltyEngineId;
        params.unavailableItemId = loyaltyEngineId;
      }

      navigate({ pathname: routes.minimalStoreLocator, params });
    }, [availableStoreIds, loyaltyEngineId, navigate, storeRestrictionMode]);

    return (
      <OfferListItem
        id={incentiveData.id}
        engineId={incentiveData.loyaltyEngineId}
        image={
          <Picture
            image={image}
            alt={imageDescription}
            height="97px"
            width="97px"
            objectFitContain
            onDisplay={onDisplay}
          />
        }
        header={incentiveName}
        description={description && <IncentiveCardDescription content={description} />}
        serviceModeStyle={
          offerServiceMode === OffersServiceMode.DELIVERY_ONLY ? 'default-outline' : 'default-solid'
        }
        promoCodeLabel={promoCodeLabel}
        onPress={handlePress}
        unavailable={!!inlineAlertMessage}
        unavailableMessage={inlineAlertMessage}
        unavailableIcon="infoOutline"
        deeplinkUnavailablePresentation={deeplinkUnavailablePresentation}
        unavailableBadgeLabel={unavailableBadgeLabel}
        onUnavailableActionPress={
          deeplinkUnavailablePresentation && inlineAlertMessage
            ? handleUnavailableActionPress
            : undefined
        }
        isWalmartOffer={isWalmartOffer(incentiveData) || isWalmartQuarterlyOffer(incentiveData)}
        isWalmartUser={isWalmartUser}
        isWalmartPaidMember={isWalmartUser && isWalmartPaidMember(loyaltyUser)}
        endDate={endDate}
        expirationDate={expirationDate}
        offerKind={incentiveData.offerKind}
      />
    );
  },
  (prev, next) => {
    return (
      prev.incentiveData.id === next.incentiveData.id &&
      prev.incentiveData.name === next.incentiveData.name &&
      prev.incentiveData.inlineAlertMessage === next.incentiveData.inlineAlertMessage &&
      prev.incentiveData.storeRestrictionMode === next.incentiveData.storeRestrictionMode &&
      (prev.incentiveData.availableStoreIds ?? []).join(',') ===
        (next.incentiveData.availableStoreIds ?? []).join(',') &&
      prev.deeplinkUnavailablePresentation === next.deeplinkUnavailablePresentation
    );
  }
);
